import type { InvoiceCustomer } from "@/core/pharmacy/types";
import type { PaymentMethod } from "@/core/treasury/types";

export type BillingDocumentKind = "proforma" | "invoice";

export type BillingDocumentSource =
  | "prescription"
  | "consultation"
  | "laboratory"
  | "imaging"
  | "prescription_partial"
  | "emergency"
  | "hospitalization"
  | "nursing"
  | "surgery"
  | "ambulance"
  | "patient_consumables";

export type BillingDocumentStatus =
  | "pending"
  | "proforma"
  | "issued"
  | "at_cashier"
  | "paid"
  | "cancelled";

export type PayerType = "patient" | "insurer" | "company";

export interface BillingDocumentLine {
  id: string;
  productId: string;
  label: string;
  unitPrice: number;
  quantity: number;
}

export interface BillingDocument {
  id: string;
  source: BillingDocumentSource;
  kind: BillingDocumentKind;
  status: BillingDocumentStatus;
  number: string;
  patientId: string;
  prescriptionId: string | null;
  /** Ligne(s) ordonnance ciblées (proforma partielle). */
  prescriptionLineIds: string[];
  labOrderId: string | null;
  imagingExamId: string | null;
  /** Acte bloc opératoire détaillé (Sprint H8b). */
  surgeryCaseId: string | null;
  /** Fiche consommables patient (Sprint H8c-1). */
  consumableChargeId: string | null;
  consultationId: string | null;
  coverageCaseId: string | null;
  admissionId: string | null;
  /** Séjour hospitalisation lié (facturation nuitée / forfait). */
  stayId: string | null;
  /** Libellé service (ex. Médecine générale) pour consultation. */
  serviceLabel: string;
  payerType: PayerType;
  payerName: string;
  /** Assureur CRM lié au patient (tiers payant). */
  insurerId: string | null;
  customer: InvoiceCustomer;
  lines: BillingDocumentLine[];
  subtotal: number;
  createdAt: string;
  updatedAt: string;
  issuedAt: string | null;
  proformaValidUntil: string | null;
  convertedFromId: string | null;
  /** Référence facture publiée sur le hub assureur. */
  hubInvoiceId: string | null;
  hubPublishedAt: string | null;
  payment: BillingPayment | null;
}

export interface BillingPaymentAttachment {
  fileName: string;
  mimeType: string;
  dataUrl: string;
}

export interface BillingPayment {
  method: PaymentMethod;
  treasuryAccountId: string;
  treasuryAccountName: string;
  amount: number;
  reference: string;
  attachment: BillingPaymentAttachment | null;
  paidAt: string;
  paidBy: string;
}

export const BILLING_DOC_STATUS_LABELS: Record<BillingDocumentStatus, string> = {
  pending: "À facturer",
  proforma: "Proforma",
  issued: "Facture émise",
  at_cashier: "En caisse",
  paid: "Payée",
  cancelled: "Annulée",
};

export const PAYER_TYPE_LABELS: Record<PayerType, string> = {
  patient: "Patient",
  insurer: "Assureur",
  company: "Entreprise",
};
